import { useState } from 'react';
import { Button, Form, Input, Alert, Typography } from 'antd';
import { LockOutlined, UserOutlined } from '@ant-design/icons';

import { signIn } from 'technical/auth/client';
import { useTranslation } from 'translations/hooks';

const { Title } = Typography;

interface LoginFormProps {
  onSuccess?: () => void;
}

interface LoginFormValues {
  username: string;
  password: string;
}

export const LoginForm = ({ onSuccess }: LoginFormProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLogin = async (values: LoginFormValues) => {
    setLoading(true);
    setError(null);

    const result = await signIn.username({
      username: values.username,
      password: values.password,
    });

    setLoading(false);

    if (result.error) {
      setError(result.error.message || t('auth.login.error'));
      return;
    }

    onSuccess?.();
  };

  return (
    <div className="w-full rounded-lg bg-white p-6 shadow-md">
      <Title level={3} className="mb-6 text-center">
        {t('auth.login.title')}
      </Title>

      {error ? (
        <Alert
          title={error}
          type="error"
          showIcon
          closable={{
            onClose: () => setError(null),
          }}
          className="mb-4"
        />
      ) : null}

      <Form<LoginFormValues>
        name="login"
        onFinish={handleLogin}
        layout="vertical"
        requiredMark={false}
      >
        <Form.Item
          name="username"
          label={t('auth.login.username.label')}
          rules={[
            { required: true, message: t('auth.login.username.required') },
          ]}
          className="pb-4"
        >
          <Input
            prefix={<UserOutlined />}
            placeholder={t('auth.login.username.placeholder')}
            size="large"
            autoComplete="username"
          />
        </Form.Item>

        <Form.Item
          name="password"
          label={t('auth.login.password.label')}
          rules={[
            { required: true, message: t('auth.login.password.required') },
          ]}
          className="pb-4"
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder={t('auth.login.password.placeholder')}
            size="large"
            autoComplete="current-password"
          />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            block
            size="large"
          >
            {t('auth.login.action.login')}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};
